import React, { useEffect, useMemo, useState } from "react";
import { Download, X } from "lucide-react";
import { apiUrl } from "../../service/apiUrl";

const FileViewModal = ({ filePath, fileName, filesByType, onClose, onDownload }) => {
  const getFileExtension = (path) => {
    if (!path) return "";
    return path.split(".").pop().toLowerCase();
  };

  const parseFileList = (value) => {
    if (!value || value === "null") return [];
    if (Array.isArray(value)) {
      return value
        .map((item) => String(item || "").trim())
        .filter(Boolean);
    }
    return String(value)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const normalizedFilesByType = useMemo(() => {
    if (filesByType && typeof filesByType === "object") {
      const entries = Object.entries(filesByType)
        .map(([key, list]) => [key, parseFileList(list)])
        .filter(([, list]) => list.length > 0);
      return Object.fromEntries(entries);
    }

    const fallbackFiles = parseFileList(filePath);
    return fallbackFiles.length > 0 ? { Files: fallbackFiles } : {};
  }, [filesByType, filePath]);

  const typeOptions = useMemo(() => Object.keys(normalizedFilesByType), [normalizedFilesByType]);
  const [selectedType, setSelectedType] = useState(typeOptions[0] || "");
  const selectedFiles = normalizedFilesByType[selectedType] || [];
  const [selectedFile, setSelectedFile] = useState(selectedFiles[0] || "");

  useEffect(() => {
    setSelectedType(typeOptions[0] || "");
  }, [typeOptions]);

  useEffect(() => {
    const nextFiles = normalizedFilesByType[selectedType] || [];
    setSelectedFile(nextFiles[0] || "");
  }, [normalizedFilesByType, selectedType]);

  const ext = getFileExtension(selectedFile);
  const downloadUrl = selectedFile ? apiUrl.downloadTool(selectedFile) : "";
  const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(downloadUrl)}`;
  const googleViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(downloadUrl)}&embedded=true`;
  
  const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  
  // Debug log
  console.log("FileViewModal - filePath:", selectedFile);
  console.log("FileViewModal - downloadUrl:", downloadUrl);

  const renderPreview = () => {
    if (!selectedFile) {
      return <p style={{ textAlign: "center", color: "#999" }}>No file to display</p>;
    }

    if (["pdf"].includes(ext)) {
      return (
        <iframe
          src={downloadUrl}
          style={{
            width: "100%",
            height: "600px",
            border: "none",
            borderRadius: "4px",
          }}
          title="File Preview"
        />
      );
    }

    if (["txt"].includes(ext)) {
      return (
        <iframe
          src={downloadUrl}
          style={{
            width: "100%",
            height: "600px",
            border: "none",
            borderRadius: "4px",
          }}
          title="File Preview"
        />
      );
    }

    if (["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(ext)) {
      // For localhost/development, show download message
      if (isLocalhost) {
        return (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
            }}
          >
            <p style={{ fontSize: "18px", marginBottom: "16px" }}>
              📄 {ext.toUpperCase()} File
            </p>
            <p style={{ color: "#666", marginBottom: "24px", fontSize: "14px" }}>
              Preview for office files works on production servers. <br />
              For local development, please download and open the file locally.
            </p>
          </div>
        );
      }

      // For production, use Office Web Viewer (primary) and keep Google gview as fallback option.
      return (
        <div>
          <iframe
            src={officeViewerUrl}
            style={{
              width: "100%",
              height: "600px",
              border: "none",
              borderRadius: "4px",
            }}
            title="File Preview"
          />
          <div style={{ marginTop: "8px", textAlign: "right" }}>
            <a
              href={googleViewerUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#1976d2",
                textDecoration: "none",
                fontSize: "12px",
                fontWeight: "600",
              }}
            >
              Open with Google Viewer
            </a>
          </div>
        </div>
      );
    }

    return (
      <div
        style={{
          textAlign: "center",
          padding: "40px",
          backgroundColor: "#f5f5f5",
          borderRadius: "4px",
        }}
      >
        <p style={{ fontSize: "18px", marginBottom: "16px" }}>File Type: {ext.toUpperCase()}</p>
        <p style={{ color: "#666", marginBottom: "24px" }}>
          Preview not available for this file type.
        </p>
        <button
          onClick={() => window.open(downloadUrl, "_blank")}
          style={{
            background: "#1976d2",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            padding: "8px 16px",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          Open in New Tab
        </button>
      </div>
    );
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 99999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "8px",
          padding: "24px",
          maxWidth: "900px",
          width: "90%",
          maxHeight: "80vh",
          overflowY: "auto",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "600" }}>
            📄 {fileName || "File Preview"}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "24px",
              padding: "0",
              color: "#666",
            }}
          >
            <X width={24} height={24} />
          </button>
        </div>

        {typeOptions.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "10px",
              marginBottom: "16px",
            }}
          >
            <div>
              <label style={{ display: "block", marginBottom: "6px", fontSize: "13px", color: "#444" }}>
                Document Type
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                }}
              >
                {typeOptions.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "6px", fontSize: "13px", color: "#444" }}>
                Select File
              </label>
              <select
                value={selectedFile}
                onChange={(e) => setSelectedFile(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                }}
              >
                {selectedFiles.map((file) => (
                  <option key={file} value={file}>
                    {file}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div style={{ marginBottom: "16px" }}>{renderPreview()}</div>

        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            style={{
              background: "#f0f0f0",
              color: "#333",
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "8px 16px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            Close
          </button>
          <a
            href={downloadUrl}
            download
            onClick={(e) => {
              if (!selectedFile) {
                e.preventDefault();
              }
            }}
            style={{
              background: "#1976d2",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "8px 16px",
              cursor: "pointer",
              fontSize: "14px",
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Download width={16} height={16} /> Download
          </a>
        </div>
      </div>
    </div>
  );
};

export default FileViewModal;
